import { dropTask } from 'ember-concurrency';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import CustomFieldsApproverReview from '../custom-fields-approver-review.gts';
import DefinitionList from '../../ui/definition-list.gts';
import ProgramReadonlyField from '../program-readonly-field.gts';
import type Store from '@ember-data/store';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type IntlService from 'ember-intl/services/intl';
import { PhArrowRight } from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';

interface TuitionAssistanceApplicationDetailsEmployeeInformationSignature {
  Args: {
    instance: TasProgramInstanceModel;
    application?: TasApplicationModel;
    profileRoute: string;
    routeModel: unknown;
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceApplicationDetailsEmployeeInformationComponent extends Component<TuitionAssistanceApplicationDetailsEmployeeInformationSignature> {
  @tracked isExpanded = false;
  @tracked pauseEmployee = false;
  @tracked showModal = false;
  @tracked holdChangeNote: string = '';
  @service declare store: Store;
  @service declare intl: IntlService;

  get relevantMultiplier() {
    return Number(this.args.application?.tasProgramLimit?.limitCoefficient) * 100;
  }

  get currentMultiplier() {
    return Number(this.employee.customAttributes.raw?.tmBalanceMultiplier) * 100;
  }

  get employee() {
    return this.args.instance.employee;
  }

  get participantId() {
    return this.employee.tasParticipant.id;
  }

  get participantIsActive() {
    return this.employee.tasParticipant.isActive;
  }

  get formValid() {
    return !!this.holdChangeNote;
  }

  get employmentType() {
    const type = this.employee.employmentType;
    switch (type) {
      case 'FULL_TIME':
        return this.intl.t('common.full_time');
      case 'PART_TIME':
        return this.intl.t('common.part_time');
      case 'EXEMPT':
        return this.intl.t('tuition_assistance.application_details.exempt');
      case 'NON_EXEMPT':
        return this.intl.t('tuition_assistance.application_details.non_exempt');
      case 'SOME_TIME':
        return this.intl.t('tuition_assistance.application_details.some_time');
      case 'PRN':
        return this.intl.t('tuition_assistance.application_details.prn');
      case 'FTE':
        return this.intl.t('tuition_assistance.application_details.fte');
      case 'PTE':
        return this.intl.t('tuition_assistance.application_details.pte');
      case 'SEAS':
        return this.intl.t('tuition_assistance.application_details.seas');
      case 'REGULAR':
        return this.intl.t('tuition_assistance.application_details.regular');
      case 'PERDIEM':
        return this.intl.t('tuition_assistance.application_details.perdiem');
      default:
        return null;
    }
  }

  toggleHoldDialog = () => {
    this.showModal = !this.showModal;
  };

  holdOrReactivateEmployee = dropTask(async () => {
    if (this.participantIsActive) {
      await this.store
        .adapterFor('tas-participant')
        .holdTasParticipant(this.participantId, { changelog: this.holdChangeNote });
    } else {
      await this.store
        .adapterFor('tas-participant')
        .removeTasParticipantHold(this.participantId, { changelog: this.holdChangeNote });
    }
    await this.employee.tasParticipant.reload();
    this.showModal = false;
    this.holdChangeNote = '';
  });

  setHoldChangeNote = (val: string) => {
    this.holdChangeNote = val;
  };

  <template>
    <Section>
      <:header>
        <LinkTo @route={{@profileRoute}} @model={{@routeModel}} class="underline tio-fg-info">
          {{t "tuition_assistance.application_details.employee_information"}}
          <PhArrowRight @size="16" class="inline-block" />
        </LinkTo>
        <p class="text-sm text-tio-gray-600">
          {{t "tuition_assistance.id"}}
          {{@instance.employee.id}}
        </p>
      </:header>
      <:body>
        <DefinitionList as |options|>
          <ProgramReadonlyField
            @name="NAME"
            @programTemplate={{@instance.tasProgramTemplate}}
            @fields={{@instance.fields}}
            as |field|
          >
            <div class={{options.wrappingClass}}>
              <options.dt>{{field.programLabel}}</options.dt>
              <options.dd>
                {{! @glint-expect-error: field.value is null or record }}
                {{field.value}}
              </options.dd>
            </div>
          </ProgramReadonlyField>
          <ProgramReadonlyField
            @name="EMPLOYEE_EMAIL"
            @programTemplate={{@instance.tasProgramTemplate}}
            @fields={{@instance.fields}}
            as |field|
          >
            <div class={{options.wrappingClass}}>
              <options.dt>{{field.programLabel}}</options.dt>
              <options.dd>
                {{! @glint-expect-error: field.value is null or record }}
                {{field.value}}
              </options.dd>
            </div>
          </ProgramReadonlyField>
          <div class={{options.wrappingClass}}>
            <options.dt>{{t "tuition_assistance.application_details.company_name"}}</options.dt>
            <options.dd>
              {{this.employee.company.name}}
            </options.dd>
          </div>
          {{#if this.employmentType}}
            <div class={{options.wrappingClass}}>
              <options.dt>{{t
                  "tuition_assistance.application_details.employment_type"
                }}</options.dt>
              <options.dd>
                {{this.employmentType}}
              </options.dd>
            </div>
          {{/if}}
          {{#if this.currentMultiplier}}
            <div class={{options.wrappingClass}}>
              <options.dt>{{t
                  "tuition_assistance.application_details.balance_multiplier"
                }}</options.dt>
              <options.dd>
                {{this.currentMultiplier}}%
              </options.dd>
            </div>
          {{/if}}
          {{#if this.relevantMultiplier}}
            <div class={{options.wrappingClass}}>
              <options.dt>{{t
                  "tuition_assistance.application_details.app_balance_multiplier"
                }}</options.dt>
              <options.dd>
                {{this.relevantMultiplier}}%
              </options.dd>
            </div>
          {{/if}}
        </DefinitionList>
        <CustomFieldsApproverReview @customFields={{@instance.customFields}} @province="EMPLOYEE" />
      </:body>
    </Section>
  </template>
}
